import { Icon, type IconType } from "materia"
import type { ReactNode } from "react"
import { modifiers } from "ui-tools"
import {
  MAX_ZOOM_FACTOR,
  MIN_ZOOM_FACTOR,
  usePDFContext,
} from "../../providers"
import s from "./Toolbar.module.scss"

export const Toolbar = ({ fileName }: { fileName: string }) => {
  const { isOutlineVisible, toggleOutline } = usePDFContext()

  return (
    <div className={s.toolbar}>
      <ToolbarSection>
        <ToolbarButton
          icon="List"
          title="Show PDF outline"
          onClick={toggleOutline}
          isActive={isOutlineVisible}
        />

        <div className={s.toolbar__title} title={fileName}>
          {fileName}
        </div>
      </ToolbarSection>

      <ToolbarSection>
        <NavigationControls />
        <div className={s.toolbar__separator} />
        <ZoomControls />
        <div className={s.toolbar__separator} />
        <FitControls />
      </ToolbarSection>

      <ToolbarSection>
        <span />
        {/* <ToolbarButton
          icon="Download"
          title="Download PDF"
          onClick={() => {
            // [TODO] https://jira.attensi.com/browse/WEB-20626
          }}
        /> */}
      </ToolbarSection>
    </div>
  )
}

const NavigationControls = () => {
  const { currentPageNumber, numberOfPages, navigateToPage } = usePDFContext()

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPage = e.target.value
    navigateToPage(Number(newPage))
  }

  return (
    <ToolbarSection>
      <div className={s.toolbar__pages}>
        <input
          className={s.toolbar__pageInput}
          value={currentPageNumber}
          type="number"
          min={1}
          max={numberOfPages}
          onChange={onChange}
        />
        <span className={s.toolbar__numberOfPages}>/</span>
        <span className={s.toolbar__numberOfPages}>{numberOfPages}</span>
      </div>

      <ToolbarButton
        icon="ChevronLight"
        iconRotate={90}
        disabled={currentPageNumber >= numberOfPages}
        title="Navigate to next page"
        onClick={() => navigateToPage(currentPageNumber + 1)}
      />

      <ToolbarButton
        disabled={currentPageNumber <= 1}
        icon="ChevronLight"
        iconRotate={270}
        title="Navigate to previous page"
        onClick={() => navigateToPage(currentPageNumber - 1)}
      />
    </ToolbarSection>
  )
}

const ZoomControls = () => {
  const { zoomFactor, zoomIn, zoomOut } = usePDFContext()

  const zoomPercentage = `${Math.round(zoomFactor * 100)}%`

  return (
    <ToolbarSection>
      <ToolbarButton
        disabled={zoomFactor <= MIN_ZOOM_FACTOR}
        icon="Minus"
        title="Zoom out"
        onClick={zoomOut}
      />

      <input className={s.toolbar__zoomInput} value={zoomPercentage} disabled />

      <ToolbarButton
        disabled={zoomFactor >= MAX_ZOOM_FACTOR}
        icon="Plus"
        title="Zoom in"
        onClick={zoomIn}
      />
    </ToolbarSection>
  )
}

const FitControls = () => {
  const { fitToWidth, fitToHeight } = usePDFContext()

  return (
    <ToolbarSection>
      <ToolbarButton
        icon="DoubleArrow"
        iconRotate={90}
        title="Fit to width"
        onClick={fitToWidth}
      />
      <ToolbarButton
        icon="DoubleArrow"
        title="Fit to height"
        onClick={fitToHeight}
      />
    </ToolbarSection>
  )
}

const ToolbarSection = ({ children }: { children: ReactNode }) => {
  return <div className={s.toolbar__section}>{children}</div>
}

const ToolbarButton = ({
  icon,
  iconRotate,
  isActive = false,
  ...buttonProps
}: {
  icon?: IconType
  iconRotate?: 0 | 90 | 180 | 270
  isActive?: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      className={modifiers(s, "toolbarButton", { isActive })}
      {...buttonProps}
    >
      {icon && <Icon icon={icon} size="medium" rotate={iconRotate} />}
    </button>
  )
}
